<!--
 * A Vue component that wraps the b-form-input element of Bootstrap Vue.
 * This element is required by default.
 *
 *
 * Properties
 * ==========
 * - name -
 * (Optional) The text that is used as id and name for input element.
 *
 * - description -
 * (Optional) A description that appears below the input element.
 *
 * - text -
 * (Optional) The text to show on the label above the input field.
 *
 * - labelSize -
 * (Optional) To control the label text size. Valid values are 'lg' for large and 'sm' for small.
 *
 * - value -
 * (Optional) To set the initial value of the input element.
 *
 * - extendedClass -
 * (Optional) Allows to manipulate the class of the root DIV element
 *
 * - enableClickEvent -
 * (Optional) Allows to throw the Click event, because this event is by defathe prevented
 * to be thrown by b-form-checkbox component.
 *
 * - expandClickArea -
 * (Optional) Expand clickable area around checkbox for a better touch experience.
 *
 *
 * Events
 * ==========
 * - input -
 * This event is sent when the selected value(s) is changed.
 * The object to sent along with this event is the inserted value from input element.
 *
 * - change -
 * This event is sent when selected value(s) is changed due to user interaction.
 * The object to sent along with this event is the inserted value from input element.
 *
 * - click -
 * This event is sent when user clicks on the element.
 * This event will only be fired for IE browser or when user sets the property enableClickEvent to true.
 * The IE browser does not have indeterminate state, subscribe to click event to simulate the indeterminate state.
 *
 * Usage
 * ==========
 * -You do not need to listen to the emitted input event from this component.
 * Just use the v-model in your parent component and the value will be updated
 * from this component automatically.
 *
 *-You can put the standard html input attributes, sucht as required,
 * autofocus, pattern, etc. directly from the parent component without explicit binding.
 * The custom attributes like listed above, should be bind.
-->

<template>
    <div id="x-checkbox-container" v-bind:class="extendedClass">
        <b-form-checkbox
            v-bind="$attrs"
            :id="name"
            ref="form_checkbox"
            :size="checkboxSize"
            v-model="checked"
            @input="$_onInput"
            @change="$_onChange"
            :indeterminate="indeterminate"
            :plain="plainMode"
            :disabled="disabled"
            :class="{ 'expanded-area': expandClickArea }"
        >
            {{ text }}
        </b-form-checkbox>
    </div>
</template>

<script>
    export default {
        inheritAttrs: false,
        name: 'x-checkbox',
        props: {
            description: { type: String, required: false },
            name: { type: String, required: false, default: 'chk-x-checkbox' },
            text: { type: String, required: false, default: '' },
            labelSize: { type: String, required: false, default: '' },
            value: { type: Boolean, required: false, default: false },
            indeterminate: { type: Boolean, default: false },
            disabled: { type: Boolean, default: false },
            plainMode: { type: Boolean, default: false },
            extendedClass: { type: [String, Object], default: '' },
            enableClickEvent: { type: Boolean, default: false },
            checkboxSize: { type: String, default: '' },
            expandClickArea: { type: Boolean, default: false }
        },

        mounted() {
            // Subscribe to click event to simulate indeterminate state in IE
            const uagent = navigator.userAgent.toLowerCase();
            const isIe = /msie/.test(uagent) || /trident/.test(uagent);
            if (isIe || this.enableClickEvent) {
                this.$refs.form_checkbox.$el.onclick = (event) => {
                    this.$emit('click', event);
                };
            }
        },

        computed: {
            checked: {
                get() {
                    return this.value;
                },
                set(input) {
                    // do nothing, one way data binding
                }
            }
        },

        methods: {
            $_onInput: function (input) {
                this.$emit('input', input);
            },

            $_onChange: function (input) {
                this.$emit('change', input);
            }
        }
    };
</script>

<style>
.expanded-area input:after {
    content: '';
    position: absolute;
    top: -15px;
    bottom: -15px;
    left: -15px;
    right: -15px;
}
</style>
